import { useAsyncData } from '~/hooks/useAsyncData'
import {
  acknowledgePhaseEnd,
  correctPhase,
  endActivePhase,
  extendActivePhase,
  getPhaseEndState,
  listPhaseHistory,
} from '~/services/phaseService'
import { todayFor } from '~/services/weightService'
import { useDatabase } from '~/storage/DatabaseContext'
import { STORE } from '~/storage/schema'
import { unwrapDate } from '~/types/dates'
import { PhaseHistorySection } from './PhaseHistorySection'

/**
 * The phase-end decision and phase history, with their own data.
 *
 * Loaded here rather than through `getEditableRecords` because this needs *every*
 * phase, not the active one, and folding a list into a record-shaped reader
 * would have made both harder to follow.
 *
 * `STORE.phases` is the only invalidation key. Every write on this screen is an
 * explicit save that closes its form, so re-reading afterwards is what should
 * happen — unlike the weekly wizard, where autosave made re-reading destroy the
 * user's position (D-073).
 */
export function PhaseHistoryEditor({ onSaved }: { onSaved: () => void }) {
  const db = useDatabase()

  const { state, reload } = useAsyncData(async () => {
    const now = new Date()
    const [endState, phases] = await Promise.all([getPhaseEndState(db, now), listPhaseHistory(db)])
    return { endState, phases, today: unwrapDate(todayFor(now)) }
  }, [STORE.phases])

  if (state.status !== 'ready') return null

  const { endState, phases, today } = state.value
  if (!endState.ok || !phases.ok) return null

  /* A write that failed must never look like one that worked, so each handler
     returns the message on failure and refreshes only on success. */
  function afterWrite(failure: string | undefined): string | undefined {
    if (failure === undefined) {
      reload()
      onSaved()
    }
    return failure
  }

  return (
    <PhaseHistorySection
      endState={endState.value}
      phases={phases.value}
      today={today}
      onEndPhase={async (request) => {
        const written = await endActivePhase(db, request, new Date())
        return afterWrite(written.ok ? undefined : written.error.message)
      }}
      onExtendPhase={async (plannedEndDate) => {
        const written = await extendActivePhase(db, plannedEndDate, new Date())
        return afterWrite(written.ok ? undefined : written.error.message)
      }}
      onAcknowledge={async () => {
        const phase = endState.value.phase
        if (phase === undefined) return 'There is no active phase.'
        const written = await acknowledgePhaseEnd(db, phase.id, new Date())
        return afterWrite(written.ok ? undefined : written.error.message)
      }}
      onCorrect={async ({ id, patch }) => {
        const written = await correctPhase(db, id, patch, new Date())
        return afterWrite(written.ok ? undefined : written.error.message)
      }}
    />
  )
}
